import { useMemo, useCallback } from 'react';
import { useData } from './useData';
import { useApp } from '../contexts/AppContext';
import { supabase } from '../config/supabase';
import { logger } from '../utils/logger';

/**
 * useVendorQuotes - Hook for vendor quote operations
 *
 * Provides:
 * - Quote data fetching
 * - Quote CRUD operations
 * - Quote selection with cost integration
 * - Quote comparison helpers
 */
export const useVendorQuotes = projectId => {
  const { showSuccess, showError } = useApp();

  // Fetch vendor quotes for this project
  const {
    data: quotes,
    isLoading: quotesLoading,
    insert: insertQuote,
    update: updateQuote,
    remove: removeQuote,
  } = useData('vendor_quotes', {
    filter: { project_id: projectId },
    enabled: !!projectId,
    realtime: true,
  });

  // Fetch quote items
  const {
    data: quoteItems,
    isLoading: quoteItemsLoading,
    insert: insertQuoteItem,
  } = useData('vendor_quote_items', {
    enabled: !!projectId,
    realtime: true,
  });

  // Fetch project items for linking quotes via active_quote_line_id
  // eslint-disable-next-line no-unused-vars
  const { data: projectItems, update: updateProjectItem } = useData('project_items', {
    filter: { project_id: projectId },
    enabled: !!projectId,
  });

  // Fetch project vendors for linking
  const { insert: insertProjectVendor, remove: removeProjectVendor } = useData('project_vendors');

  // Create lookup maps
  const quotesMap = useMemo(() => {
    if (!quotes) return new Map();
    return new Map(quotes.map(q => [q.id, q]));
  }, [quotes]);

  // projectItemsMap available for future use (e.g., checking current quote links)
  // const projectItemsMap = useMemo(() => {
  //   if (!projectItems) return new Map();
  //   return new Map(projectItems.map(pi => [pi.id, pi]));
  // }, [projectItems]);

  /**
   * Get quotes for a specific project item
   */
  const getQuotesForItem = useCallback(
    itemId => {
      if (!quotes || !quoteItems) return [];

      // Find quote items that include this project item
      const relevantQuoteItemIds = quoteItems
        .filter(qi => qi.project_item_ids?.includes(itemId))
        .map(qi => qi.quote_id);

      // Get unique quote IDs and return the quotes
      const uniqueQuoteIds = [...new Set(relevantQuoteItemIds)];
      return quotes.filter(q => uniqueQuoteIds.includes(q.id));
    },
    [quotes, quoteItems],
  );

  /**
   * Get the selected quote for an item (if any)
   */
  const getSelectedQuoteForItem = useCallback(
    itemId => {
      const itemQuotes = getQuotesForItem(itemId);
      return itemQuotes.find(q => q.status === 'selected');
    },
    [getQuotesForItem],
  );

  /**
   * Create a new quote with line items
   */
  const createQuote = useCallback(
    async (quoteData, lineItems) => {
      try {
        // Generate share token for the quote
        const shareToken = crypto.randomUUID();

        // Create the quote
        const result = await insertQuote({
          ...quoteData,
          project_id: projectId,
          share_token: shareToken,
          share_enabled: false,
        });

        // useData insert returns an array from .select()
        const newQuote = Array.isArray(result) ? result[0] : result;
        if (!newQuote?.id) {
          throw new Error('Failed to create quote');
        }

        // Create line items
        for (const lineItem of lineItems) {
          await insertQuoteItem({
            quote_id: newQuote.id,
            project_item_ids: lineItem.project_item_ids || [],
            item_type_ids: lineItem.item_type_ids || [],
            excluded_item_ids: lineItem.excluded_item_ids || [],
            line_price: lineItem.line_price || null,
            line_notes: lineItem.line_notes || null,
            ...(lineItem.display_data ? { display_data: lineItem.display_data } : {}),
          });
        }

        showSuccess('הצעת המחיר נוצרה בהצלחה');
        return newQuote;
      } catch (error) {
        logger.error('Error creating quote', error, { projectId });
        showError('שגיאה ביצירת הצעת המחיר');
        throw error;
      }
    },
    [projectId, insertQuote, insertQuoteItem, showSuccess, showError],
  );

  /**
   * Helper: process a single project item during quote selection.
   * Sets active_quote_line_id and creates vendor link.
   */
  const processItemForQuoteSelection = useCallback(
    async (itemId, lineItem, quote) => {
      // 1. Set active_quote_line_id on the project item
      await updateProjectItem({
        id: itemId,
        data: { active_quote_line_id: lineItem.id },
      });

      // 2. Link vendor to item
      try {
        await insertProjectVendor({
          project_item_id: itemId,
          master_vendor_id: quote.master_vendor_id,
        });
      } catch (vendorError) {
        // Vendor might already be linked, ignore duplicate errors
        if (!vendorError.message?.includes('duplicate')) {
          logger.warn('Error linking vendor:', vendorError);
        }
      }
    },
    [updateProjectItem, insertProjectVendor],
  );

  /**
   * Select a quote - links quote line items to project items via active_quote_line_id.
   * Does NOT overwrite user's cost_override - quote price is fetched via the link for display.
   */
  const selectQuote = useCallback(
    async quoteId => {
      try {
        // Try local cache first, then fetch from DB (handles newly created quotes)
        let quote = quotesMap.get(quoteId);
        if (!quote) {
          const { data: fetchedQuote, error } = await supabase
            .from('vendor_quotes')
            .select('*')
            .eq('id', quoteId)
            .single();
          if (error || !fetchedQuote) {
            throw new Error('Quote not found');
          }
          quote = fetchedQuote;
        }

        // Get all quote items for this quote (fetch from DB if not in local cache)
        let quoteLineItems = quoteItems?.filter(qi => qi.quote_id === quoteId) || [];
        if (quoteLineItems.length === 0) {
          const { data: fetchedItems } = await supabase
            .from('vendor_quote_items')
            .select('*')
            .eq('quote_id', quoteId);
          quoteLineItems = fetchedItems || [];
        }

        // Process all project items through helper
        for (const lineItem of quoteLineItems) {
          for (const itemId of lineItem.project_item_ids || []) {
            await processItemForQuoteSelection(itemId, lineItem, quote);
          }
        }

        // Ensure total_price is set before selecting (compute from line items if missing)
        // This guarantees useProjectFinancials always has a non-null total_price for selected quotes.
        if (quote.total_price == null) {
          const computedTotal = quoteLineItems.reduce((sum, qi) => {
            return sum + (parseFloat(qi.line_price) || 0);
          }, 0);
          await updateQuote({
            id: quoteId,
            data: { total_price: computedTotal > 0 ? computedTotal : 0, status: 'selected' },
          });
        } else {
          // Update quote status to selected
          await updateQuote({
            id: quoteId,
            data: { status: 'selected' },
          });
        }

        // Create vendor type assignment if vendor has a vendor_type_id
        // This assigns the vendor to the project for their vendor type role
        try {
          const { data: vendorData } = await supabase
            .from('master_vendors')
            .select('vendor_type_id')
            .eq('id', quote.master_vendor_id)
            .single();

          if (vendorData?.vendor_type_id && projectId) {
            const { error: assignmentError } = await supabase
              .from('project_vendor_type_assignments')
              .upsert(
                {
                  project_id: projectId,
                  vendor_type_id: vendorData.vendor_type_id,
                  master_vendor_id: quote.master_vendor_id,
                  updated_at: new Date().toISOString(),
                },
                {
                  onConflict: 'project_id,vendor_type_id',
                },
              );

            if (assignmentError) {
              logger.warn('Error creating vendor type assignment:', assignmentError);
            }
          }
        } catch (assignmentError) {
          logger.warn('Error creating vendor type assignment:', assignmentError);
        }

        // Reject other quotes that overlap on the same project items
        const affectedItemIds = new Set();
        quoteLineItems.forEach(qi => {
          qi.project_item_ids?.forEach(id => affectedItemIds.add(id));
        });

        const otherQuotes =
          quotes?.filter(
            q => q.id !== quoteId && q.status !== 'rejected' && q.status !== 'selected',
          ) || [];

        for (const otherQuote of otherQuotes) {
          const otherQuoteItems = quoteItems?.filter(qi => qi.quote_id === otherQuote.id) || [];
          const hasOverlap = otherQuoteItems.some(qi =>
            qi.project_item_ids?.some(id => affectedItemIds.has(id)),
          );

          if (hasOverlap) {
            await updateQuote({
              id: otherQuote.id,
              data: { status: 'rejected' },
            });
          }
        }

        return true;
      } catch (error) {
        logger.error('Error selecting quote', error, { quoteId, projectId });
        showError('שגיאה בבחירת הצעת המחיר');
        throw error;
      }
    },
    [
      quotesMap,
      quoteItems,
      quotes,
      projectId,
      updateQuote,
      processItemForQuoteSelection,
      showSuccess,
      showError,
    ],
  );

  /**
   * Helper: process a single project item during quote unselection.
   * Clears active_quote_line_id and removes vendor link.
   */
  const processItemForQuoteUnselection = useCallback(
    async (itemId, quote) => {
      // 1. Clear active_quote_line_id
      await updateProjectItem({
        id: itemId,
        data: { active_quote_line_id: null },
      });

      // 2. Remove vendor link
      try {
        const { data: projectVendor } = await supabase
          .from('project_vendors')
          .select('id')
          .eq('project_item_id', itemId)
          .eq('master_vendor_id', quote.master_vendor_id)
          .single();

        if (projectVendor) {
          await removeProjectVendor(projectVendor.id);
        }
      } catch (vendorError) {
        logger.warn('Error removing vendor link:', vendorError);
      }
    },
    [updateProjectItem, removeProjectVendor],
  );

  /**
   * Unselect a quote - clears active_quote_line_id from project items.
   * User's cost_override is preserved and will be displayed again.
   */
  const unselectQuote = useCallback(
    async quoteId => {
      try {
        const quote = quotesMap.get(quoteId);
        if (!quote) {
          throw new Error('Quote not found');
        }

        // Get all quote items for this quote
        const quoteLineItems = quoteItems?.filter(qi => qi.quote_id === quoteId) || [];

        // Process all project items through helper
        for (const qi of quoteLineItems) {
          for (const itemId of qi.project_item_ids || []) {
            await processItemForQuoteUnselection(itemId, quote);
          }
        }

        // Update quote status back to quote_received
        await updateQuote({
          id: quoteId,
          data: { status: 'quote_received' },
        });

        showSuccess('בחירת ההצעה בוטלה');
        return true;
      } catch (error) {
        logger.error('Error unselecting quote', error, { quoteId, projectId });
        showError('שגיאה בביטול בחירת ההצעה');
        throw error;
      }
    },
    [
      quotesMap,
      quoteItems,
      projectId,
      updateQuote,
      processItemForQuoteUnselection,
      showSuccess,
      showError,
    ],
  );

  /**
   * Generate a shareable link for a quote
   */
  const generateShareLink = useCallback(
    async (quoteId, { routePrefix = '/quote/view/' } = {}) => {
      try {
        const quote = quotesMap.get(quoteId);
        if (!quote) {
          throw new Error('Quote not found');
        }

        // Enable sharing if not already enabled
        if (!quote.share_enabled) {
          await updateQuote({
            id: quoteId,
            data: { share_enabled: true },
          });
        }

        // Return the share URL
        const baseUrl = window.location.origin;
        return `${baseUrl}${routePrefix}${quote.share_token}`;
      } catch (error) {
        logger.error('Error generating share link', error, { quoteId });
        showError('שגיאה ביצירת קישור שיתוף');
        throw error;
      }
    },
    [quotesMap, updateQuote, showError],
  );

  /**
   * Disable sharing for a quote
   */
  const disableSharing = useCallback(
    async quoteId => {
      try {
        await updateQuote({
          id: quoteId,
          data: { share_enabled: false },
        });
        showSuccess('השיתוף בוטל');
      } catch (error) {
        logger.error('Error disabling share', error, { quoteId });
        showError('שגיאה בביטול השיתוף');
        throw error;
      }
    },
    [updateQuote, showSuccess, showError],
  );

  /**
   * Check if a quote is expired
   */
  const isQuoteExpired = useCallback(quote => {
    if (!quote?.validity_date) return false;
    return new Date(quote.validity_date) < new Date();
  }, []);

  /**
   * Get quote statistics for the project
   */
  const quoteStats = useMemo(() => {
    if (!quotes) return { total: 0, pending: 0, selected: 0, expired: 0 };

    return {
      total: quotes.length,
      pending: quotes.filter(q => q.status === 'quote_received' || q.status === 'pending').length,
      selected: quotes.filter(q => q.status === 'selected').length,
      expired: quotes.filter(q => isQuoteExpired(q)).length,
    };
  }, [quotes, isQuoteExpired]);

  return {
    // Data
    quotes,
    quoteItems,
    quotesMap,
    quoteStats,

    // Loading states
    isLoading: quotesLoading || quoteItemsLoading,

    // Query helpers
    getQuotesForItem,
    getSelectedQuoteForItem,
    isQuoteExpired,

    // Mutations
    createQuote,
    updateQuote,
    removeQuote,
    selectQuote,
    unselectQuote,

    // Sharing
    generateShareLink,
    disableSharing,
  };
};

export default useVendorQuotes;
